import { Box, Stack, Button, Alert, TextField, CircularProgress } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useState } from "react";

import { ControlledTextField } from "../forms/ControlledTextField";
import { ControlledTextPasswordFieldFilled } from "../forms/ControlledTextPasswordField";
import { ControlledCountryPhoneField } from "../forms/ControlledCountryPhoneField";

import { HttpAuth, syncTokensAfterLogin } from "../../http";
import { userStorage } from "../../utils/storage";

import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { getCouponFieldStyle } from "../Checkout/CouponStyles";
import { useTheme } from "../../util/themes";

import { digitsOnly, validateInternationalPhone } from "@ecommerce/shared-utils";


const PHONE_MAX_DIGITS = 20;

const ALLOWED_COUNTRY_CODES = new Set(["54", "598", "55", "595", "591", "56"]);

const schema = yup.object({
    nombre: yup.string().required("El nombre es obligatorio"),
    apellido: yup.string().required("El apellido es obligatorio"),

    codigoArea: yup
        .string()
        .required("Seleccionó un país.")
        .test("country-code", "Seleccionó un país válido.", (val) => {
            const cc = digitsOnly(String(val ?? ""));
            return !!cc && ALLOWED_COUNTRY_CODES.has(cc);
        }),

    telefono: yup
        .string()
        .required("Ingresá código de área y número.")
        .test("valid-phone", "Ingresá un telófono válido.", function (val) {
            const phoneRaw = String(val ?? "");
            const parent = this.parent as any;
            const countryRaw = String(parent?.codigoArea ?? "");
            const res = validateInternationalPhone(countryRaw, phoneRaw);
            return res.ok ? true : this.createError({ message: res.message });
        }),

    mail: yup.string().email("Email inválido").required("El email es obligatorio"),
    password: yup.string().required("La contraseña es obligatoria"),
    passwordConfirm: yup
        .string()
        .required("Debe repetir la contraseña")
        .oneOf([yup.ref("password")], "Las contraseñas no coinciden"),
    fechaNacimiento: yup.string().required("La fecha de nacimiento es obligatoria"),
});

export type RegisterUserForm = yup.InferType<typeof schema>;

interface Props {
    onSuccess: (user: { nombre: string; apellido: string }) => void;
    onLoadingChange?: (loading: boolean) => void;
}

export const UserRegisterForm = ({ onSuccess, onLoadingChange }: Props) => {
    const theme = useTheme();
    const couponFieldStyle = getCouponFieldStyle(theme as any);
    
    const {
        control,
        handleSubmit,
        register,
        getValues,
        setValue,
        trigger,
    } = useForm<RegisterUserForm>({
        resolver: yupResolver(schema),
        defaultValues: {
            nombre: "",
            apellido: "",
            codigoArea: "",
            telefono: "",
            mail: "",
            password: "",
            passwordConfirm: "",
            fechaNacimiento: "",
        },
    });

    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const onSubmit = async (data: RegisterUserForm) => {
        setError("");
        setSaving(true);
        onLoadingChange?.(true);

        try {
            const codigoAreaDigits = digitsOnly(data.codigoArea);
            const telefonoDigits = digitsOnly(data.telefono);

            const resp = await HttpAuth.registerUser(
                data.nombre,
                data.apellido,
                codigoAreaDigits,
                telefonoDigits,
                data.mail,
                data.password,
                data.fechaNacimiento
            );

            if (resp.tieneError) {
                setError(resp.descripcionError);
                return;
            }

            userStorage.save(
                resp.idUsuario,
                resp.nombre,
                resp.apellido,
                resp.apellidoNombre,
                resp.codUsuarioEstado,
                resp.accessToken,
                resp.refreshToken,
                resp.idPerfil,
                resp.codigoArea,
                resp.telefono
            );


            syncTokensAfterLogin(resp.accessToken, resp.refreshToken);

            onSuccess({ nombre: resp.nombre, apellido: resp.apellido });
        } catch (e: any) {
            setError(e?.message ?? "Error registrando usuario");
        } finally {
            setSaving(false);
            onLoadingChange?.(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2.5}>
                <ControlledTextField control={control} name="nombre" label="Nombre" />
                <ControlledTextField control={control} name="apellido" label="Apellido" />

                <ControlledCountryPhoneField<RegisterUserForm>
                    control={control}
                    getValues={getValues}
                    setValue={setValue}
                    trigger={trigger}
                    countryCodeName={"codigoArea"}
                    phoneName={"telefono"}
                    required
                    phoneMaxLength={PHONE_MAX_DIGITS}
                    defaultCountryIso2="AR"
                    phoneHelperText={'Ingresá código de área y número. Ej AR celular: " 9 11 1234 5678".'}
                />

                <ControlledTextField control={control} name="mail" label="Email" />

                <ControlledTextPasswordFieldFilled
                    control={control}
                    name="password"
                    label="Contraseña"
                    sx={couponFieldStyle}
                />
                <ControlledTextPasswordFieldFilled
                    control={control}
                    name="passwordConfirm"
                    label="Repetir contraseña"
                    sx={couponFieldStyle}
                />

                <TextField
                    type="date"
                    label="Fecha de nacimiento"
                    InputLabelProps={{ shrink: true }}
                    sx={couponFieldStyle}
                    {...register("fechaNacimiento")}
                />

                <Button
                    variant="contained"
                    type="submit"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={18} /> : undefined}
                    sx={{ ...BUTTON_STYLES.invertedPrimary, width: "100%" }}
                >
                    {saving ? "Registrando..." : "Registrarme"}
                </Button>

                {error && <Alert severity="error">{error}</Alert>}
            </Stack>
        </Box>
    );
};














